import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ReportsService } from '../../../@theme/services/reports.service';


@Component({
  selector: 'app-fincenreports',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './fincenreports.component.html',
  styleUrl: './fincenreports.component.scss'
})
export class FincenreportsComponent implements OnInit, OnDestroy {
  reportForm: FormGroup;
  quarterDates: Array<{ date: string; display: string }> = [];
  isLoading = false;
  noMatchesMessage = '';
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private reportsService: ReportsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadQuarterDates();
  }

  private buildForm(): void {
    this.reportForm = this.fb.group({
      selectedDate: ['', Validators.required]
    });

    // Clear the "no matches" message when a different date is picked
    this.subscription.add(
      this.reportForm.get('selectedDate')!.valueChanges.subscribe(() => this.noMatchesMessage = '')
    );
  }

  private loadQuarterDates(): void {
    this.isLoading = true;
    this.subscription.add(
      this.reportsService.getquarterdatesReports().subscribe({
        next: (response) => {
          // API returns array of objects with DisplayDate and Date properties
          if (Array.isArray(response)) {
            this.quarterDates = response.map((item: any) => ({
              date: item.Date || item.date,  // Use the Date property from API
              display: item.DisplayDate || item.displayDate  // Use DisplayDate from API
            }));
          }
          
          // Pre-select the most recent date (first in array, as API returns sorted descending)
          if (this.quarterDates.length > 0) {
            // Find the most recent date
            const mostRecentDate = this.quarterDates.reduce((latest, current) => {
              return new Date(current.date) > new Date(latest.date) ? current : latest;
            });
            this.reportForm.patchValue({ selectedDate: mostRecentDate.date });
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(err.message || 'Failed to load quarter dates', 'Error');
          this.isLoading = false;
        }
      })
    );
  }

  private formatQuarterDate(dateStr: string): string {
    // This method is no longer needed since DisplayDate comes from API
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US');
  }

  createReport(): void {
    if (this.reportForm.invalid) {
      this.toastr.warning('Please select a date', 'Validation');
      return;
    }

    const selectedDate = this.reportForm.get('selectedDate')?.value;
    this.isLoading = true;
    this.noMatchesMessage = '';

    this.subscription.add(
      this.reportsService.getGetFinCenReportPdf({ UploadDate: selectedDate }).subscribe({
        next: (blob) => {
          // API returns 204 No Content when there are no matches for the date
          if (!blob || blob.size === 0) {
            this.noMatchesMessage = `There are no FinCEN matches for ${new Date(selectedDate).toLocaleDateString('en-US')}.`;
            this.isLoading = false;
            return;
          }

          // Handle the PDF blob - trigger download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `FinCEN_Report_${new Date(selectedDate).toLocaleDateString('en-US').replace(/\//g, '-')}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
          
          this.toastr.success('FinCEN report created successfully', 'Success');
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(err.message || 'Failed to create report', 'Error');
          this.isLoading = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
